import { useState } from "react";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { toast } from "sonner";
import { Instagram, Phone, Mail } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function Contact() {
  useScrollReveal();

  const [form, setForm] = useState({ name: "", email: "", wedding_date: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Merci de remplir tous les champs obligatoires.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSubmitted(true);
        toast.success("Message envoyé avec succès !");
        setForm({ name: "", email: "", wedding_date: "", message: "" });
      } else {
        toast.error("Une erreur s'est produite. Veuillez réessayer.");
      }
    } catch {
      toast.error("Une erreur s'est produite. Veuillez réessayer.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="page-fade" data-testid="contact-page" style={{ paddingTop: "72px" }}>

      {/* Header */}
      <section style={{ padding: "100px 24px 80px", backgroundColor: "#F7F3EA", textAlign: "center" }}>
        <p style={{ fontSize: "10px", letterSpacing: "5px", textTransform: "uppercase", color: "rgba(61,22,2,0.4)", marginBottom: "20px" }}>
          Contact
        </p>
        <h1 style={{
          fontFamily: "'Noto Serif Ethiopic', Georgia, serif",
          fontSize: "clamp(2.5rem, 6vw, 5rem)",
          fontWeight: 300, color: "#3D1602",
          letterSpacing: "-0.02em", lineHeight: 1.1, marginBottom: "20px",
        }}>
          Parlons de votre mariage.
        </h1>
        <p style={{ fontFamily: "'Noto Serif Ethiopic', Georgia, serif", fontStyle: "italic", fontSize: "22px", color: "rgba(61,22,2,0.5)" }}>
          L'aventure commence ici.
        </p>
      </section>

      {/* Main content */}
      <section style={{ padding: "100px 24px", backgroundColor: "#F7F3EA" }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <div style={{
            display: "grid",
            gridTemplateColumns: "1fr",
            gap: "80px",
          }}
            className="lg:grid-cols-2"
          >
            {/* Left — info */}
            <div>
              <p className="scroll-reveal" style={{ fontSize: "10px", letterSpacing: "5px", textTransform: "uppercase", color: "#6D5E56", marginBottom: "24px" }}>
                Prenons contact
              </p>
              <h2 className="scroll-reveal delay-1" style={{
                fontFamily: "'Noto Serif Ethiopic', Georgia, serif",
                fontSize: "clamp(1.8rem, 3vw, 2.5rem)",
                fontWeight: 300, color: "#3D1602",
                lineHeight: 1.25, marginBottom: "24px",
              }}>
                Chaque grande histoire commence par une conversation.
              </h2>
              <p className="scroll-reveal delay-2" style={{
                fontSize: "14px", lineHeight: "1.9",
                color: "#6D5E56", fontWeight: 300,
                marginBottom: "48px",
              }}>
                Vous avez un projet de mariage en tête ? Une date, une idée, une envie — même floue ? Partagez-le avec moi. Le premier appel découverte est gratuit et sans engagement. C'est juste une conversation pour voir si nous sommes faits pour travailler ensemble.
              </p>

              {/* Contact info */}
              <div className="scroll-reveal delay-2" style={{ display: "flex", flexDirection: "column", gap: "24px", marginBottom: "48px" }}>
                <a href="mailto:ellaa.wedding@gmail.com"
                  style={{ display: "flex", alignItems: "center", gap: "16px", textDecoration: "none", color: "#3D1602" }}>
                  <div style={{ width: "40px", height: "40px", border: "1px solid rgba(61,22,2,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Mail size={16} color="#6D5E56" />
                  </div>
                  <div>
                    <p style={{ fontSize: "10px", letterSpacing: "3px", textTransform: "uppercase", color: "#6D5E56", marginBottom: "2px" }}>Email</p>
                    <p style={{ fontSize: "14px", fontWeight: 300 }}>ellaa.wedding@gmail.com</p>
                  </div>
                </a>

                <a href="https://api.whatsapp.com/send?phone=33783600328" target="_blank" rel="noopener noreferrer"
                  style={{ display: "flex", alignItems: "center", gap: "16px", textDecoration: "none", color: "#3D1602" }}>
                  <div style={{ width: "40px", height: "40px", border: "1px solid rgba(61,22,2,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Phone size={16} color="#6D5E56" />
                  </div>
                  <div>
                    <p style={{ fontSize: "10px", letterSpacing: "3px", textTransform: "uppercase", color: "#6D5E56", marginBottom: "2px" }}>WhatsApp</p>
                    <p style={{ fontSize: "14px", fontWeight: 300 }}>+33 7 83 60 03 28</p>
                  </div>
                </a>

                <a href="https://www.instagram.com/ellaa.wedding?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==" target="_blank" rel="noopener noreferrer"
                  data-testid="contact-instagram"
                  style={{ display: "flex", alignItems: "center", gap: "16px", textDecoration: "none", color: "#3D1602" }}>
                  <div style={{ width: "40px", height: "40px", border: "1px solid rgba(61,22,2,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Instagram size={16} color="#6D5E56" />
                  </div>
                  <div>
                    <p style={{ fontSize: "10px", letterSpacing: "3px", textTransform: "uppercase", color: "#6D5E56", marginBottom: "2px" }}>Instagram</p>
                    <p style={{ fontSize: "14px", fontWeight: 300 }}>@ellaa.wedding</p>
                  </div>
                </a>
              </div>

              {/* Calendly CTA */}
              <div className="scroll-reveal delay-3">
                <p style={{ fontSize: "10px", letterSpacing: "3px", textTransform: "uppercase", color: "#6D5E56", marginBottom: "16px" }}>
                  Ou réservez directement
                </p>
                <a
                  href="https://calendly.com/ellaa-wedding/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-calendly"
                  className="btn-primary"
                >
                  Planifier un appel découverte
                </a>
              </div>
            </div>

            {/* Right — Form */}
            <div className="scroll-reveal delay-1">
              {submitted ? (
                <div style={{
                  padding: "64px 40px",
                  border: "1px solid rgba(61,22,2,0.1)",
                  textAlign: "center",
                }}>
                  <p style={{ fontFamily: "'Noto Serif Ethiopic', Georgia, serif", fontStyle: "italic", fontSize: "40px", color: "#3D1602", marginBottom: "16px" }}>Merci</p>
                  <h3 style={{ fontFamily: "'Noto Serif Ethiopic', Georgia, serif", fontSize: "1.4rem", fontWeight: 300, color: "#3D1602", marginBottom: "16px" }}>
                    Votre message a bien été envoyé.
                  </h3>
                  <p style={{ fontSize: "14px", lineHeight: "1.8", color: "#6D5E56", fontWeight: 300 }}>
                    Je reviens vers vous sous 48 heures. Dans l'intervalle, n'hésitez pas à me suivre sur Instagram pour vous inspirer.
                  </p>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-testid="contact-form"
                  style={{ display: "flex", flexDirection: "column", gap: "32px" }}
                >
                  <div>
                    <label style={{ fontSize: "10px", letterSpacing: "3px", textTransform: "uppercase", color: "#6D5E56", display: "block", marginBottom: "8px" }}>
                      Nom & Prénom *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Votre nom complet"
                      className="form-input"
                      data-testid="contact-name"
                      required
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: "10px", letterSpacing: "3px", textTransform: "uppercase", color: "#6D5E56", display: "block", marginBottom: "8px" }}>
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="votre@email.com"
                      className="form-input"
                      data-testid="contact-email"
                      required
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: "10px", letterSpacing: "3px", textTransform: "uppercase", color: "#6D5E56", display: "block", marginBottom: "8px" }}>
                      Date souhaitée du mariage
                    </label>
                    <input
                      type="text"
                      name="wedding_date"
                      value={form.wedding_date}
                      onChange={handleChange}
                      placeholder="Ex: Juin 2026"
                      className="form-input"
                      data-testid="contact-wedding-date"
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: "10px", letterSpacing: "3px", textTransform: "uppercase", color: "#6D5E56", display: "block", marginBottom: "8px" }}>
                      Message *
                    </label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      placeholder="Parlez-moi de votre projet..."
                      rows={6}
                      className="form-input"
                      data-testid="contact-message"
                      required
                      style={{ resize: "none" }}
                    />
                  </div>

                  <button
                    type="submit"
                    data-testid="contact-submit"
                    disabled={submitting}
                    className="btn-primary"
                    style={{ border: "none", cursor: submitting ? "not-allowed" : "pointer", opacity: submitting ? 0.7 : 1 }}
                  >
                    {submitting ? "Envoi en cours..." : "Envoyer le message"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
